import {FC, useEffect} from "react";
import styled from "styled-components";
import {useDispatch, useSelector} from "react-redux";
import {useTranslation} from "react-i18next";
import Slider, {Settings} from "react-slick";
import {createConnextraScriptTag} from "modules/utils";
import {getGameCards} from "modules/selectors";
import {showErrorModal} from "modules/actions";
import {Card} from "components";
import bg from "assets/images/cards-bg.png";
import bgMobile from "assets/images/cards-bg-mobile.png";

const Wrapper = styled.div`
	width: 100%;
	background: url(${bgMobile}) no-repeat 18px -25px;
	background-size: cover;

	@media (min-width: 769px) {
		background: url(${bg}) no-repeat center;
		background-size: 100%;
		padding: 0 50px 80px;
	}
`;

const Container = styled.div`
	width: 100%;
	padding: 0 20px 70px;
	max-width: 360px;
	margin: 0 auto;

	@media (min-width: 769px) {
		max-width: 1440px;
		padding: 0 50px 80px;
	}
`;

const DesktopContainer = styled.div`
	display: none;

	@media (min-width: 769px) {
		width: 100%;
		display: flex;
		flex-direction: row;
		flex-wrap: wrap;
		justify-content: center;
		gap: 20px;
		max-width: 1000px;
		margin: 0 auto;
	}
`;

const MobileContainer = styled.div`
	.slick-dots {
		button:before {
			font-size: 8px;
			opacity: 1;
			color: white;
		}

		.slick-active {
			button:before {
				color: ${({theme}) => theme.color.secondary};
			}
		}

		li {
			width: 12px;
			margin: 0;
		}
	}

	.slick-track {
		display: flex !important;
		gap: 10px;

		&:after,
		&:before {
			content: none;
		}
	}

	.slick-slide {
		height: inherit !important;

		& > div {
			height: 100%;

			& > div {
				height: 100%;
			}
		}
	}

	@media (min-width: 769px) {
		display: none;
	}
`;

const Title = styled.div`
	font-family: "Khand", sans-serif;
	font-weight: 700;
	font-size: 24px;
	line-height: 120%;
	margin-bottom: 24px;
	color: ${({theme}) => theme.color.tertiary};
	text-align: left;

	@media (min-width: 769px) {
		margin-bottom: 20px;
	}
`;

export const LandingCards: FC = () => {
	const dispatch = useDispatch();
	const {t} = useTranslation();
	const cards = useSelector(getGameCards);

	useEffect(() => {
		createConnextraScriptTag(
			"https://zz.connextra.com/dcs/tagController/tag/8ce716f59752/f2p_gamezone_homepage"
		);

		const eventHandler = () => {
			dispatch(
				showErrorModal({
					message: "modal.error.no_connection",
				})
			);
		};

		window.addEventListener("offline", eventHandler);

		return () => {
			window.removeEventListener("offline", eventHandler);
		};
	}, [dispatch]);

	const settings: Settings = {
		dots: true,
		infinite: false,
		arrows: false,
		speed: 500,
		slidesToShow: 1,
		slidesToScroll: 1,
	};

	return (
		<Wrapper>
			<Container>
				<Title>{t("main.cards.title")}</Title>
				<DesktopContainer>
					{cards.map((card) => (
						<Card key={card.id} card={card} />
					))}
				</DesktopContainer>

				<MobileContainer>
					<Slider {...settings}>
						{cards.map((card) => (
							<Card key={card.id} card={card} />
						))}
					</Slider>
				</MobileContainer>
			</Container>
		</Wrapper>
	);
};
